import { useAddNotification } from '@va/dashboard/util-hooks';
import { ListIcon, ResetIcon, TrashCanIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import {
  CustomDomainPlatformType,
  CustomDomainRecord,
  CustomDomainsApi,
  CustomDomainTarget,
} from '@va/shared/api/custom-domains';
import { CustomDomainAction, ViewDNSRecordsModal } from '@va/shared/custom-domains';
import { useConfirmationDialogContext } from '@va/shared/util-confirmation';
import { selectAgencyId } from '@va/standalone/shared/selectors';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAgencyCustomSupportCenterDomains } from '../../shared/useAgencyCustomDomains';
import { DeleteCustomDomainConfirmationModal } from '../confirmation-modals/DeleteCustomDomainConfirmationModal';
import { PendingCustomDomainCancellationConfirmationModal } from '../confirmation-modals/PendingCustomDomainCancellationConfirmationModal';
import { CustomSupportCenterDomainCreationModal } from './CustomSupportCenterDomainCreationModal';

export const useCustomSuportCenterDomainActions = () => {
  const agencyId = useSelector(selectAgencyId);

  const translate = useTranslate();

  const { mutate } = useAgencyCustomSupportCenterDomains();

  const { showErrorNotification } = useAddNotification();

  const { confirm } = useConfirmationDialogContext();

  const cancelPendingCustomDomain = useCallback(
    async (customDomainId: number) => {
      if (!agencyId) return;
      try {
        await CustomDomainsApi.cancelPendingCompanyDomain({ companyId: agencyId, customDomainId: customDomainId });
        await mutate();
      } catch (error) {
        console.error(error);
        showErrorNotification(undefined, CustomDomainsApi.extractApiErrorMessage(error, translate));
      }
    },
    [agencyId, mutate, showErrorNotification, translate],
  );

  const deleteInactiveCustomDomain = useCallback(
    async (customDomainId: number) => {
      if (!agencyId) return;
      try {
        await CustomDomainsApi.deleteCompanyDomain({ companyId: agencyId, customDomainId: customDomainId });
        await mutate();
      } catch (error) {
        console.error(error);
        showErrorNotification(undefined, CustomDomainsApi.extractApiErrorMessage(error, translate));
      }
    },
    [agencyId, mutate, showErrorNotification, translate],
  );

  const openViewDNSRecordsModal = useCallback(
    (domain: string) => {
      confirm(
        () => {},
        ({ onClose, onConfirm }) => (
          <ViewDNSRecordsModal
            platform={CustomDomainPlatformType.AWL}
            isOpen={true}
            title={translate('customDomain.labels.dashboardDNSRecordsTitle')}
            expectedDNSRecordsCount={2}
            onConfirm={onConfirm}
            onClose={onClose}
            domain={domain}
            target={CustomDomainTarget.supportCenter}
            onFailure={(err) => {
              showErrorNotification(undefined, err);
            }}
          />
        ),
      );
    },
    [confirm, showErrorNotification, translate],
  );

  const activeCustomDomainActions = useCallback(
    (customDomain: CustomDomainRecord): CustomDomainAction[] => {
      return [
        {
          icon: <ListIcon className='w-3 h-3' color='gray' />,
          label: translate('customDomain.labels.viewDNSRecords'),
          onClick: () => {
            openViewDNSRecordsModal(customDomain.domain);
          },
        },
        {
          icon: <TrashCanIcon className='w-3 h-3' color='var(--color-negative)' />,
          label: translate('button.delete'),
          onClick: () => {
            confirm(() => {
              deleteInactiveCustomDomain(customDomain.id);
            }, DeleteCustomDomainConfirmationModal);
          },
        },
      ];
    },
    [confirm, deleteInactiveCustomDomain, openViewDNSRecordsModal, translate],
  );

  const pendingCustomDomainActions = useCallback(
    (customDomain: CustomDomainRecord): CustomDomainAction[] => {
      return [
        {
          icon: <ListIcon className='w-3 h-3' color='gray' />,
          label: translate('customDomain.labels.viewDNSRecords'),
          onClick: () => {
            openViewDNSRecordsModal(customDomain.domain);
          },
        },
        {
          icon: <TrashCanIcon className='w-3 h-3' color='var(--color-negative)' />,
          label: translate('button.cancel'),
          onClick: () => {
            confirm(() => {
              cancelPendingCustomDomain(customDomain.id);
            }, PendingCustomDomainCancellationConfirmationModal);
          },
        },
      ];
    },
    [cancelPendingCustomDomain, confirm, openViewDNSRecordsModal, translate],
  );

  const inactiveCustomDomainsActions = useCallback(
    (customDomain: CustomDomainRecord): CustomDomainAction[] => {
      return [
        {
          icon: <ResetIcon className='w-3 h-3' color='var(--color-negative)' />,
          label: translate('button.retryActivation'),
          onClick: () => {
            confirm(
              () => {},
              ({ onClose }) => (
                <CustomSupportCenterDomainCreationModal
                  domain={customDomain.domain}
                  isOpen={true}
                  closeModal={onClose}
                  customDomainId={customDomain.id}
                  onSuccess={function (): void {
                    mutate();
                  }}
                  companyId={agencyId ?? ''}
                />
              ),
            );
          },
        },
        {
          icon: <TrashCanIcon className='w-3 h-3' color='var(--color-negative)' />,
          label: translate('button.delete'),
          onClick: () => {
            confirm(() => {
              deleteInactiveCustomDomain(customDomain.id);
            }, DeleteCustomDomainConfirmationModal);
          },
        },
      ];
    },
    [confirm, deleteInactiveCustomDomain, translate],
  );

  return {
    activeCustomDomainActions,
    pendingCustomDomainActions,
    inactiveCustomDomainsActions,
  };
};
